import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Upload, Loader2, CheckCircle2, AlertTriangle, XCircle, FileQuestion,
  Download, Trash2, HelpCircle, Plus, ArrowUpRight, ExternalLink
} from "lucide-react";
import { toast } from "sonner";

/**
 * GSTR-2B Reconciliation Assistant.
 *
 * Modal that lets the user upload a GSTR-2B JSON downloaded from the GST
 * portal and reconciles it against their expenses + purchases in the same
 * period. Surfaces four buckets:
 *   - Matched (green)
 *   - Amount Mismatch (amber)
 *   - In App, Not in 2B (amber — chase supplier)
 *   - In 2B, Not in App (red — record it, one-click prefill)
 */
export default function GSTReconciliationModal({ open, onOpenChange, fromDate, toDate }) {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [uploads, setUploads] = useState([]);
  const [selectedUploadId, setSelectedUploadId] = useState("");
  const [uploading, setUploading] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState("matched");

  const fetchUploads = useCallback(async () => {
    try {
      const r = await api.get("/reconciliation/gstr2b/uploads");
      setUploads(r.data || []);
    } catch { /* silent — offline is fine */ }
  }, []);

  useEffect(() => {
    if (open) {
      fetchUploads();
    } else {
      // Reset when modal closes so a re-open starts fresh
      setResult(null);
      setSelectedUploadId("");
      setActiveTab("matched");
    }
  }, [open, fetchUploads]);

  const runReconcile = useCallback(async (uploadId) => {
    if (!uploadId) return;
    setReconciling(true);
    setResult(null);
    try {
      const r = await api.post("/reconciliation/gstr2b/reconcile", {
        upload_id: uploadId,
        from_date: fromDate,
        to_date: toDate,
      });
      setResult(r.data);
      const c = r.data.summary.counts;
      // Default to the bucket that has issues (most actionable first)
      if (c.in_2b_not_in_app > 0) setActiveTab("in_2b_not_in_app");
      else if (c.mismatched > 0) setActiveTab("mismatched");
      else if (c.in_app_not_in_2b > 0) setActiveTab("in_app_not_in_2b");
      else setActiveTab("matched");
      toast.success(`Reconciled — ${c.matched} matched, ${c.in_2b_not_in_app} missing in app`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reconciliation failed");
    } finally {
      setReconciling(false);
    }
  }, [fromDate, toDate]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("File must be under 20MB");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/reconciliation/gstr2b/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded — ${r.data.count} invoices (period ${r.data.period || "—"})`);
      await fetchUploads();
      setSelectedUploadId(r.data.upload_id);
      await runReconcile(r.data.upload_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed — check the JSON format");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const deleteUpload = async (id) => {
    try {
      await api.delete(`/reconciliation/gstr2b/uploads/${id}`);
      await fetchUploads();
      if (selectedUploadId === id) {
        setSelectedUploadId("");
        setResult(null);
      }
      toast.success("Upload deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const exportCsv = async () => {
    if (!selectedUploadId) return;
    try {
      const r = await api.post("/reconciliation/gstr2b/reconcile.csv", {
        upload_id: selectedUploadId,
        from_date: fromDate,
        to_date: toDate,
      }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `gstr2b_reconciliation_${result?.upload?.period || "export"}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Reconciliation exported");
    } catch {
      toast.error("CSV export failed");
    }
  };

  const addAsPurchase = (recon) => {
    const gstRate = recon.taxable_value > 0
      ? Math.round(((recon.cgst + recon.sgst + recon.igst) / recon.taxable_value) * 100)
      : 18;
    const prefill = {
      vendor_name: recon.supplier_name,
      gst_number: recon.ctin,
      bill_number: recon.invoice_number,
      bill_date: recon.invoice_date,
      date: recon.invoice_date,
      amount: recon.invoice_value,
      line_items: [{
        name: `Invoice ${recon.invoice_number}`,
        hsn_code: "",
        quantity: 1,
        unit_price: recon.taxable_value,
        gst_rate_pct: [0, 5, 12, 18, 28].includes(gstRate) ? gstRate : 18,
        cgst: recon.cgst,
        sgst: recon.sgst,
        igst: recon.igst,
        taxable_value: recon.taxable_value,
      }],
    };
    navigate("/purchases", { state: { prefill, source: "gstr2b_reconciliation" } });
    onOpenChange(false);
  };

  const c = result?.summary?.counts || {};
  const t = result?.summary?.tax_totals || {};

  const TAB_META = [
    { id: "matched", label: "Matched", icon: CheckCircle2, color: "#10b981",
      count: c.matched || 0, rows: result?.matched || [] },
    { id: "mismatched", label: "Amount Mismatch", icon: AlertTriangle, color: "#f59e0b",
      count: c.mismatched || 0, rows: result?.mismatched || [] },
    { id: "in_app_not_in_2b", label: "In App, Not in 2B", icon: FileQuestion, color: "#f59e0b",
      count: c.in_app_not_in_2b || 0, rows: result?.in_app_not_in_2b || [] },
    { id: "in_2b_not_in_app", label: "In 2B, Not in App", icon: XCircle, color: "#ef4444",
      count: c.in_2b_not_in_app || 0, rows: result?.in_2b_not_in_app || [] },
  ];
  const active = TAB_META.find((tab) => tab.id === activeTab) || TAB_META[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[1200px] max-h-[92vh] overflow-hidden flex flex-col" data-testid="gstr2b-recon-modal">
        <DialogHeader>
          <DialogTitle className="font-outfit text-xl flex items-center gap-2">
            <ArrowUpRight className="h-5 w-5 text-[#4338ca]" />
            GSTR-2B Reconciliation
          </DialogTitle>
          <DialogDescription className="text-sm text-[#64748b]">
            Compare supplier-filed invoices against your ledger. Ledger window: {fromDate} → {toDate}
          </DialogDescription>
        </DialogHeader>

        {/* Upload / picker strip */}
        <div className="border-b border-[#e2e8f0] pb-3 flex flex-wrap items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={handleUpload}
            data-testid="gstr2b-upload-input"
          />
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="gstr2b-upload-btn"
            className="bg-[#4338ca] hover:bg-[#3730a3] rounded-lg h-9"
          >
            {uploading ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-2" />}
            {uploading ? "Uploading…" : "Upload GSTR-2B JSON"}
          </Button>

          {uploads.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#64748b]">or reuse:</span>
              <select
                value={selectedUploadId}
                onChange={(e) => {
                  setSelectedUploadId(e.target.value);
                  if (e.target.value) runReconcile(e.target.value);
                }}
                className="h-9 text-sm border border-[#e2e8f0] rounded-lg px-2 bg-white min-w-[240px]"
                data-testid="gstr2b-upload-picker"
              >
                <option value="">Pick a previous upload…</option>
                {uploads.map((u) => (
                  <option key={u.upload_id} value={u.upload_id}>
                    {u.period || "—"} · {u.filename} · {new Date(u.uploaded_at).toLocaleDateString()}
                  </option>
                ))}
              </select>
              {selectedUploadId && (
                <button type="button" onClick={() => deleteUpload(selectedUploadId)}
                  className="text-red-500 hover:text-red-600 p-1" title="Delete this upload"
                  data-testid="gstr2b-upload-delete">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}

          <details className="ml-auto text-xs text-[#64748b] group">
            <summary className="cursor-pointer inline-flex items-center gap-1 hover:text-[#4338ca]">
              <HelpCircle className="h-3.5 w-3.5" /> How to download GSTR-2B JSON
            </summary>
            <div className="absolute right-4 mt-2 bg-white border border-[#e2e8f0] shadow-lg rounded-lg p-3 w-80 z-30 text-xs text-[#64748b] leading-relaxed">
              1. Log in to <a href="https://returns.gst.gov.in" target="_blank" rel="noopener noreferrer" className="text-[#4338ca] underline">returns.gst.gov.in</a><br />
              2. Go to <b>Returns Dashboard</b> → pick the period<br />
              3. Under <b>GSTR-2B</b> click <b>DOWNLOAD</b> → <b>GENERATE JSON FILE</b><br />
              4. Once generated, click <b>Download</b> — you&apos;ll get a .json file<br />
              5. Upload that file here
            </div>
          </details>

          {result && (
            <Button size="sm" variant="outline" onClick={exportCsv} data-testid="gstr2b-recon-csv-btn" className="h-9">
              <Download className="h-3.5 w-3.5 mr-1.5" /> CSV
            </Button>
          )}
        </div>

        {/* Loading / empty state */}
        {reconciling && (
          <div className="flex-1 flex items-center justify-center py-16" data-testid="gstr2b-recon-loading">
            <Loader2 className="h-5 w-5 mr-2 animate-spin text-[#4338ca]" />
            <span className="text-sm text-[#64748b]">Reconciling…</span>
          </div>
        )}

        {!reconciling && !result && (
          <div className="flex-1 flex flex-col items-center justify-center py-16 text-center" data-testid="gstr2b-recon-empty">
            <ArrowUpRight className="h-12 w-12 text-[#e2e8f0] mb-3" />
            <p className="text-sm text-[#64748b] max-w-md">
              Upload your latest <b>GSTR-2B JSON</b> to see which of your recorded purchases
              your suppliers have filed, and which invoices show up in your 2B that you
              haven&apos;t recorded yet.
            </p>
            <p className="text-xs text-[#94a3b8] mt-2">Your data stays private — the file is parsed on your account only.</p>
          </div>
        )}

        {result && (
          <>
            {/* Summary tabs */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-3 pb-2">
              {TAB_META.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`text-left rounded-xl border p-3 transition ${
                      isActive
                        ? "border-[#4338ca] bg-[#4338ca]/5 shadow-sm"
                        : "border-[#e2e8f0] hover:border-[#cbd5e1] bg-white"
                    }`}
                    data-testid={`gstr2b-tab-${tab.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="h-7 w-7 rounded-lg flex items-center justify-center" style={{ background: `${tab.color}22` }}>
                        <Icon className="h-3.5 w-3.5" style={{ color: tab.color }} />
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-[#94a3b8] font-medium">{tab.label}</p>
                        <p className="text-lg font-outfit font-bold text-[#0f172a] leading-tight" data-testid={`gstr2b-count-${tab.id}`}>{tab.count}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Tax totals strip */}
            <div className="text-[11px] text-[#64748b] pb-2 border-b border-[#e2e8f0] flex flex-wrap gap-x-4 gap-y-1" data-testid="gstr2b-tax-totals">
              <span>Matched tax (2B): <span className="font-mono text-[#0f172a]">{formatINR(t.matched_2b || 0)}</span></span>
              <span>· App tax on matched: <span className="font-mono text-[#0f172a]">{formatINR(t.matched_app || 0)}</span></span>
              <span>· <span className="text-red-500">Unclaimed (in 2B only):</span> <span className="font-mono font-semibold text-red-500" data-testid="gstr2b-unclaimed-total">{formatINR(t.in_2b_only || 0)}</span></span>
              <span>· <span className="text-amber-500">Unfiled by supplier (in app only):</span> <span className="font-mono font-semibold text-amber-500" data-testid="gstr2b-unfiled-total">{formatINR(t.in_app_only || 0)}</span></span>
            </div>

            {/* Active-tab table */}
            <div className="flex-1 overflow-auto -mx-6 px-6 pt-2">
              {active.rows.length === 0 ? (
                <div className="py-16 text-center text-sm text-[#94a3b8]" data-testid={`gstr2b-empty-${active.id}`}>
                  Nothing in this bucket — {active.id === "matched" ? "no matches yet" : "all clear!"}
                </div>
              ) : (
                <ReconTable rows={active.rows} bucket={active.id} onAddAsPurchase={addAsPurchase} />
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ReconTable({ rows, bucket, onAddAsPurchase }) {
  return (
    <table className="w-full text-sm" data-testid={`gstr2b-table-${bucket}`}>
      <thead className="sticky top-0 bg-white z-10">
        <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
          <th className="text-left py-2.5 px-2 text-[10px] text-[#64748b] font-semibold uppercase tracking-wider">Supplier</th>
          <th className="text-left py-2.5 px-2 text-[10px] text-[#64748b] font-semibold uppercase tracking-wider">Invoice No.</th>
          <th className="text-left py-2.5 px-2 text-[10px] text-[#64748b] font-semibold uppercase tracking-wider">Date</th>
          <th className="text-right py-2.5 px-2 text-[10px] text-[#64748b] font-semibold uppercase tracking-wider">Taxable</th>
          <th className="text-right py-2.5 px-2 text-[10px] text-[#64748b] font-semibold uppercase tracking-wider">CGST</th>
          <th className="text-right py-2.5 px-2 text-[10px] text-[#64748b] font-semibold uppercase tracking-wider">SGST</th>
          <th className="text-right py-2.5 px-2 text-[10px] text-[#64748b] font-semibold uppercase tracking-wider">IGST</th>
          <th className="text-right py-2.5 px-2 text-[10px] text-[#64748b] font-semibold uppercase tracking-wider">Total</th>
          <th className="text-right py-2.5 px-2 text-[10px] text-[#64748b] font-semibold uppercase tracking-wider">Action</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => {
          const isMissing = bucket === "in_2b_not_in_app";
          const isUnfiled = bucket === "in_app_not_in_2b";
          const primary = isMissing ? row.recon : row.ledger;
          const supplier = isMissing ? row.recon.supplier_name : row.ledger.vendor_name;
          const gstin = isMissing ? row.recon.ctin : row.ledger.gstin;
          const invNum = isMissing ? row.recon.invoice_number : row.ledger.invoice_number;
          const date = isMissing ? row.recon.invoice_date : row.ledger.invoice_date;
          const taxable = isMissing ? row.recon.taxable_value : row.ledger.taxable_value;
          const cgst = isMissing ? row.recon.cgst : row.ledger.cgst;
          const sgst = isMissing ? row.recon.sgst : row.ledger.sgst;
          const igst = isMissing ? row.recon.igst : row.ledger.igst;
          const total = isMissing ? row.recon.invoice_value : row.ledger.amount;
          const rowKey = `${bucket}-${invNum}-${gstin}-${i}`;
          const diff = row.mismatch?.diffs;

          return (
            <tr key={rowKey} className="border-b border-[#f1f5f9] hover:bg-[#f8fafc]" data-testid={`gstr2b-row-${bucket}-${i}`}>
              <td className="py-2.5 px-2">
                <div className="text-[#0f172a] font-medium">{supplier || "—"}</div>
                <div className="text-[10px] text-[#94a3b8] font-mono">{gstin || "—"}</div>
              </td>
              <td className="py-2.5 px-2 text-[#0f172a] font-mono text-xs">{invNum || "—"}</td>
              <td className="py-2.5 px-2 text-[#64748b] text-xs whitespace-nowrap">{date || "—"}</td>
              <td className="py-2.5 px-2 text-right font-mono tabular-nums text-[#0f172a]">{formatINR(taxable)}</td>
              <td className="py-2.5 px-2 text-right font-mono tabular-nums text-[#64748b]">{cgst ? formatINR(cgst) : "—"}</td>
              <td className="py-2.5 px-2 text-right font-mono tabular-nums text-[#64748b]">{sgst ? formatINR(sgst) : "—"}</td>
              <td className="py-2.5 px-2 text-right font-mono tabular-nums text-[#64748b]">{igst ? formatINR(igst) : "—"}</td>
              <td className="py-2.5 px-2 text-right font-mono tabular-nums font-semibold text-[#0f172a]">{formatINR(total)}</td>
              <td className="py-2.5 px-2 text-right">
                {isMissing && (
                  <Button size="sm" onClick={() => onAddAsPurchase(row.recon)}
                    data-testid={`gstr2b-add-as-purchase-${i}`}
                    className="text-[11px] h-7 bg-[#4338ca] hover:bg-[#3730a3] rounded-md">
                    <Plus className="h-3 w-3 mr-1" /> Add as Purchase
                  </Button>
                )}
                {isUnfiled && (
                  <Badge className="bg-amber-50 text-amber-600 text-[10px] px-2 py-0.5">
                    Chase supplier
                  </Badge>
                )}
                {bucket === "mismatched" && diff && (
                  <div className="text-[10px] text-right leading-tight">
                    <div className="text-red-500 font-mono">Δ tax {formatINR(diff.tax_total)}</div>
                    {Math.abs(diff.taxable_value) > 1 && (
                      <div className="text-[#64748b]">Δ taxable {formatINR(diff.taxable_value)}</div>
                    )}
                  </div>
                )}
                {bucket === "matched" && (
                  <a href="#" onClick={(e) => e.preventDefault()} className="text-[#10b981] inline-flex items-center gap-0.5 text-[10px]">
                    <CheckCircle2 className="h-3 w-3" />
                  </a>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
